import { useState, useMemo, useCallback, useEffect } from "react";
import { Search, X, Filter, Download, ChevronDown, ChevronLeft, ChevronRight, FileText, Receipt, Edit, Trash2, Plus, LayoutGrid, List, CalendarDays, StickyNote, Mail } from "lucide-react";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, addMonths, subMonths, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { getBackendUrl } from "@/utils/apiBase";

const API = `${getBackendUrl()}/api`;

const STATUS_CONFIG = {
  pending: { label: "En attente", bg: "#fef9c3", color: "#854d0e", border: "#fde68a", col: 0 },
  confirmed: { label: "Confirmée", bg: "#dbeafe", color: "#1e40af", border: "#93c5fd", col: 1 },
  paid: { label: "Payée", bg: "#dcfce7", color: "#166534", border: "#86efac", col: 2 },
};
const STATUS_ORDER = ["pending", "confirmed", "paid"];

const PACKAGE_LABELS = { "3h": "3h", "5h": "5h", "7h": "7h" };

export const BookingManager = ({ bookings, onRefresh, onEdit, onDelete, onGenerateQuote, onGenerateInvoice, activePromotion, calculatePriceWithPromotion }) => {
  const [view, setView] = useState("board");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterPackage, setFilterPackage] = useState("all");
  const [filterBooth, setFilterBooth] = useState("all");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [drawerBooking, setDrawerBooking] = useState(null);
  const [notes, setNotes] = useState({});
  const [sortCol, setSortCol] = useState("event_date");
  const [sortDir, setSortDir] = useState("asc");
  const [dragId, setDragId] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date());
  const [invoiceModal, setInvoiceModal] = useState(null); // { booking, email }
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [signInputs, setSignInputs] = useState({}); // { [bookingId]: { by, date } }

  // Keep the drawer in sync when bookings list refreshes (e.g. after Edit save)
  useEffect(() => {
    if (!drawerBooking) return;
    const fresh = bookings.find((bk) => bk.id === drawerBooking.id);
    if (fresh && fresh !== drawerBooking) {
      setDrawerBooking(fresh);
    }
  }, [bookings, drawerBooking]);

  // Date -> booth count map
  const dateBoothCount = useMemo(() => {
    const c = {};
    bookings.forEach(b => {
      if (b.event_date && ["pending", "paid", "confirmed"].includes(b.payment_status)) {
        c[b.event_date] = (c[b.event_date] || 0) + 1;
      }
    });
    return c;
  }, [bookings]);

  // Filtering
  const filtered = useMemo(() => {
    return bookings.filter(b => {
      if (filterStatus !== "all" && b.payment_status !== filterStatus) return false;
      if (filterPackage !== "all" && b.package_id !== filterPackage) return false;
      if (filterMonth) {
        if (!b.event_date || !b.event_date.startsWith(filterMonth)) return false;
      }
      if (filterBooth !== "all") {
        const cnt = dateBoothCount[b.event_date] || 0;
        if (filterBooth === "partial" && cnt !== 1) return false;
        if (filterBooth === "full" && cnt < 2) return false;
      }
      if (search) {
        const s = search.toLowerCase();
        const hay = `${b.customer_name} ${b.customer_email} ${b.event_location} ${b.event_date}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [bookings, filterStatus, filterPackage, filterMonth, filterBooth, search, dateBoothCount]);

  const activeFilterCount = [filterStatus !== "all", filterMonth, filterPackage !== "all", filterBooth !== "all", search].filter(Boolean).length;

  // Quick status change
  const changeStatus = useCallback(async (id, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status`, { payment_status: newStatus });
      toast.success("Statut mis à jour");
      onRefresh();
    } catch (e) { toast.error("Erreur"); }
  }, [onRefresh]);

  // Drag and drop
  const handleDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (dragId) { changeStatus(dragId, newStatus); setDragId(null); }
  };

  // Save notes
  const saveNote = async (bookingId, note) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { admin_notes: note });
      setNotes(prev => ({ ...prev, [bookingId]: note }));
      toast.success("Note enregistrée");
    } catch (e) { toast.error("Erreur"); }
  };

  // Mark booking as manually signed
  const markSigned = async (bookingId) => {
    const input = signInputs[bookingId] || {};
    const by = (input.by || "").trim();
    const date = input.date || new Date().toISOString().slice(0, 10);
    if (!by) {
      toast.error("Veuillez saisir le nom du signataire");
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/bookings/${bookingId}/mark-signed`, {
        signed_by: by,
        signed_date: date,
      });
      toast.success("Devis marqué comme signé");
      // Optimistic local update
      setDrawerBooking((prev) => prev && prev.id === bookingId ? {
        ...prev,
        signed: true,
        signed_by: by,
        signed_at: r.data?.signed_at,
        signed_display: r.data?.signed_display,
        payment_status: r.data?.payment_status || prev.payment_status,
      } : prev);
      if (typeof window !== "undefined") {
        // Trigger parent refresh via event so the kanban column updates
        window.dispatchEvent(new CustomEvent("booking-updated", { detail: { id: bookingId } }));
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erreur lors de l'enregistrement de la signature"));
    }
  };

  // Send invoice email
  const openInvoiceModal = (b) => {
    setInvoiceModal({ booking: b, email: b.customer_email || "" });
  };

  const sendInvoiceEmail = async () => {
    if (!invoiceModal) return;
    setSendingInvoice(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/${invoiceModal.booking.id}/send-invoice-email`, { email: invoiceModal.email });
      toast.success(`Facture envoyée à ${invoiceModal.email}`);
      setInvoiceModal(null);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erreur d'envoi"));
    } finally { setSendingInvoice(false); }
  };

  // CSV export
  const exportCSV = () => {
    const headers = "Date,Client,Email,Téléphone,Forfait,Lieu,Statut,Prix\n";
    const rows = filtered.map(b => `${b.event_date},${b.customer_name},${b.customer_email},${b.customer_phone},${b.package_name},${b.event_location},${b.payment_status},${b.total_price}`).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "reservations.csv"; a.click();
  };

  // Sorted for list view
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortCol] || "", vb = b[sortCol] || "";
      if (sortCol === "total_price") { va = Number(va) || 0; vb = Number(vb) || 0; }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  // Global stats
  const totalCount = bookings.length;
  const nextFullDate = Object.entries(dateBoothCount).filter(([d, c]) => c >= 2 && d >= new Date().toISOString().split("T")[0]).sort()[0];

  // ===== CARD COMPONENT =====
  const BookingCard = ({ b, compact }) => {
    const price = calculatePriceWithPromotion(b);
    const boothCnt = dateBoothCount[b.event_date] || 0;
    const st = STATUS_CONFIG[b.payment_status] || STATUS_CONFIG.pending;

    // Payment method badge — visible on Payée cards
    const paidBadge = b.payment_status === "paid" ? (
      b.payment_method === "stripe" ? (
        <span
          data-testid={`payment-badge-stripe-${b.id}`}
          style={{ background: "#2d7a4f", color: "#fff", fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px", borderRadius: 50, display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          💳 Stripe
        </span>
      ) : (
        <span
          data-testid={`payment-badge-paid-${b.id}`}
          style={{ background: "#b8977e", color: "#fff", fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px", borderRadius: 50 }}
        >
          ✓ Payée
        </span>
      )
    ) : null;

    return (
      <div
        draggable
        onDragStart={(e) => handleDragStart(e, b.id)}
        onClick={() => setDrawerBooking(b)}
        style={{ background: "#fff", borderRadius: "0.75rem", border: "1px solid #f0e4da", padding: "0.85rem", cursor: "pointer", transition: "box-shadow 0.2s, transform 0.15s" }}
        className="hover:shadow-md hover:-translate-y-0.5"
        data-testid={`booking-card-${b.id}`}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.4rem" }}>
          <div>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.9rem", color: "#1a1a1a", display: "flex", alignItems: "center", gap: "0.35rem" }}>
              {b.customer_name}
              {b.quote_accepted && (
                <span data-testid={`signed-quote-icon-${b.id}`} title={`Devis signé le ${b.quote_accepted_at || ""}`} style={{ fontSize: "0.85rem" }}>✍️</span>
              )}
            </p>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: "#999" }}>{b.event_type || "Événement"} — {b.event_location}</p>
          </div>
          {/* Quick status button */}
          <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
            <select
              value={b.payment_status}
              onChange={(e) => changeStatus(b.id, e.target.value)}
              style={{ fontSize: "0.7rem", fontWeight: 600, padding: "0.2rem 0.4rem", borderRadius: 50, border: `1.5px solid ${st.border}`, background: st.bg, color: st.color, cursor: "pointer", appearance: "none", paddingRight: "1rem" }}
              data-testid={`status-select-${b.id}`}
            >
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: "#666" }}>
            {b.event_date ? format(parseISO(b.event_date), "dd MMM yyyy", { locale: fr }) : "—"}
            {b.event_start_time ? ` à ${b.event_start_time}` : ""}
          </span>
          <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#b8977e", background: "#fef9f5", padding: "0.1rem 0.4rem", borderRadius: 50, border: "1px solid #e8d5c4" }}>{b.package_name || b.package_id || "—"}</span>
          {boothCnt > 0 && (
            <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "0.1rem 0.4rem", borderRadius: 50, background: boothCnt >= 2 ? "#fee2e2" : "#fff7ed", color: boothCnt >= 2 ? "#991b1b" : "#c2410c", border: boothCnt >= 2 ? "1px solid #fca5a5" : "1px solid #fed7aa" }}>
              {boothCnt}/2 PB
            </span>
          )}
          {paidBadge}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {price.discount > 0 && <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: "#bbb", textDecoration: "line-through", marginRight: "0.3rem" }}>{price.original.toFixed(0)}€</span>}
            <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.05rem", color: "#b8977e" }}>{price.final.toFixed(0)}€</span>
          </div>
          <div style={{ display: "flex", gap: "0.3rem" }} onClick={e => e.stopPropagation()}>
            <button onClick={() => onGenerateQuote(b)} title="Devis" style={iconBtnStyle}><FileText style={{ width: 14, height: 14 }} /></button>
            <button onClick={() => onGenerateInvoice(b)} title="Facture" style={iconBtnStyle}><Receipt style={{ width: 14, height: 14 }} /></button>
            <button onClick={() => openInvoiceModal(b)} title="Envoyer facture par email" style={iconBtnStyle}><Mail style={{ width: 14, height: 14 }} /></button>
            <button onClick={() => onEdit(b)} title="Modifier" style={iconBtnStyle}><Edit style={{ width: 14, height: 14 }} /></button>
            <button onClick={() => onDelete(b)} title="Supprimer" style={{ ...iconBtnStyle, color: "#ef4444" }}><Trash2 style={{ width: 14, height: 14 }} /></button>
          </div>
        </div>
      </div>
    );
  };

  // ===== SIDEBAR FILTERS =====
  const Sidebar = () => (
    <div style={{ width: sidebarOpen ? 260 : 0, minWidth: sidebarOpen ? 260 : 0, overflow: "hidden", transition: "all 0.3s", borderRight: sidebarOpen ? "1px solid #f0e4da" : "none", background: "#fafafa", padding: sidebarOpen ? "1.2rem" : 0, flexShrink: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a" }}>Filtres</span>
        <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#999" }}><X style={{ width: 16, height: 16 }} /></button>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label style={filterLabelStyle}>Rechercher</label>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom, email, lieu..." style={filterInputStyle} data-testid="filter-search" />
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label style={filterLabelStyle}>Statut</label>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={filterInputStyle} data-testid="filter-status">
          <option value="all">Tous</option>
          {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label style={filterLabelStyle}>Mois</label>
        <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={filterInputStyle} data-testid="filter-month" />
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label style={filterLabelStyle}>Forfait</label>
        <select value={filterPackage} onChange={e => setFilterPackage(e.target.value)} style={filterInputStyle} data-testid="filter-package">
          <option value="all">Tous</option>
          {Object.entries(PACKAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label style={filterLabelStyle}>Photobooth</label>
        <select value={filterBooth} onChange={e => setFilterBooth(e.target.value)} style={filterInputStyle} data-testid="filter-booth">
          <option value="all">Tous</option>
          <option value="partial">1/2 restant</option>
          <option value="full">Complet (2/2)</option>
        </select>
      </div>

      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#b8977e", fontWeight: 600 }}>{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</p>

      {activeFilterCount > 0 && (
        <button onClick={() => { setSearch(""); setFilterStatus("all"); setFilterMonth(""); setFilterPackage("all"); setFilterBooth("all"); }} style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );

  // ===== DETAIL DRAWER =====
  const Drawer = () => {
    if (!drawerBooking) return null;
    const b = drawerBooking;
    const price = calculatePriceWithPromotion(b);
    const boothCnt = dateBoothCount[b.event_date] || 0;
    const st = STATUS_CONFIG[b.payment_status] || STATUS_CONFIG.pending;
    const noteVal = notes[b.id] !== undefined ? notes[b.id] : (b.admin_notes || "");

    return (
      <>
        <div onClick={() => setDrawerBooking(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 50 }} />
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(420px, 90vw)", background: "#fff", zIndex: 51, boxShadow: "-8px 0 30px rgba(0,0,0,0.12)", overflowY: "auto", display: "flex", flexDirection: "column" }} data-testid="booking-drawer">
          {/* Header */}
          <div style={{ background: "#1a1a1a", padding: "1.2rem 1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <div>
              <p style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.1rem", color: "#fff" }}>{b.customer_name}</p>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#b8977e" }}>{b.event_type || "Événement"}</p>
            </div>
            <button onClick={() => setDrawerBooking(null)} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X style={{ width: 16, height: 16, color: "#fff" }} />
            </button>
          </div>

          <div style={{ padding: "1.5rem", flex: 1 }}>
            {/* Status */}
            <div style={{ marginBottom: "1.2rem" }}>
              <label style={{ ...filterLabelStyle, marginBottom: "0.3rem" }}>Statut</label>
              <select value={b.payment_status} onChange={e => { changeStatus(b.id, e.target.value); setDrawerBooking({ ...b, payment_status: e.target.value }); }} style={{ ...filterInputStyle, fontWeight: 600, background: st.bg, color: st.color, borderColor: st.border }}>
                {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
              </select>
            </div>

            {/* Info grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.2rem" }}>
              {[
                { label: "Email", value: b.customer_email },
                { label: "Téléphone", value: b.customer_phone },
                { label: "Date", value: b.event_date ? format(parseISO(b.event_date), "dd MMMM yyyy", { locale: fr }) : "—" },
                { label: "Heure", value: b.event_start_time || "—" },
                { label: "Lieu", value: b.event_location },
                { label: "Photobooth", value: `${boothCnt}/2` },
              ].map((item, i) => (
                <div key={i}>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: "#999", textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.label}</p>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#1a1a1a", fontWeight: 500 }}>{item.value || "—"}</p>
                </div>
              ))}
            </div>

            {/* Package + price */}
            <div style={{ background: "#f5ede8", borderRadius: "0.75rem", padding: "1rem", marginBottom: "1.2rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: "0.3rem" }}>
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a" }}>{b.package_name}</p>
                {b.payment_status === "paid" && (
                  b.payment_method === "stripe" ? (
                    <span data-testid={`drawer-payment-badge-stripe-${b.id}`} style={{ background: "#2d7a4f", color: "#fff", fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px", borderRadius: 50, display: "inline-flex", alignItems: "center", gap: 4 }}>
                      💳 Stripe
                    </span>
                  ) : (
                    <span data-testid={`drawer-payment-badge-paid-${b.id}`} style={{ background: "#b8977e", color: "#fff", fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px", borderRadius: 50 }}>
                      ✓ Payée
                    </span>
                  )
                )}
              </div>
              {b.description && <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#777", marginBottom: "0.3rem" }}>{b.description}</p>}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.5rem" }}>
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#666" }}>Total</span>
                <div>
                  {price.discount > 0 && <span style={{ textDecoration: "line-through", color: "#bbb", fontSize: "0.8rem", marginRight: "0.3rem" }}>{price.original.toFixed(0)}€</span>}
                  <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.3rem", color: "#b8977e" }}>{price.final.toFixed(0)}€</span>
                </div>
              </div>
            </div>

            {/* Acompte / Solde / Contrat */}
            {(b.promo_applied && b.promo_applied.discount_amount > 0) && (
              <div data-testid={`drawer-promo-badge-${b.id}`} style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", marginBottom: "0.8rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#2d7a4f", fontWeight: 600, fontSize: "0.85rem" }}>
                  🎉 Code {b.promo_applied.code}
                  {b.promo_applied.discount_type === "percentage" ? ` (-${Math.round(b.promo_applied.discount_value)}%)` : ""}
                </span>
                <span style={{ color: "#2d7a4f", fontWeight: 700, fontSize: "0.9rem" }}>
                  -{Number(b.promo_applied.discount_amount).toFixed(2)}€
                </span>
              </div>
            )}
            {(b.deposit_amount > 0 || b.deposit_paid || b.contract_signed) && (
              <div data-testid={`drawer-deposit-section-${b.id}`} style={{ background: "#faf8f5", border: "1px solid #e8d5c4", borderRadius: "0.75rem", padding: "1rem", marginBottom: "1.2rem" }}>
                <p style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, color: "#2d2319", fontSize: "0.95rem", marginBottom: "0.6rem" }}>
                  Paiements & Contrat
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                  <span style={{ color: "#5a5a5a" }}>
                    Acompte 30%{b.deposit_amount > 0 && ` (${b.deposit_amount.toFixed(2)}€)`}
                  </span>
                  <span data-testid={`drawer-deposit-status-${b.id}`} style={{ fontWeight: 600, color: b.deposit_paid ? "#2d7a4f" : "#f59e0b" }}>
                    {b.deposit_paid ? "✓ Payé" : "⏳ En attente"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
                  <span style={{ color: "#5a5a5a" }}>
                    Solde 70%{b.balance_amount > 0 && ` (${b.balance_amount.toFixed(2)}€)`}
                  </span>
                  <span data-testid={`drawer-balance-status-${b.id}`} style={{ fontWeight: 600, color: b.balance_paid ? "#2d7a4f" : "#9a9a9a" }}>
                    {b.balance_paid ? "✓ Payé" : "⏳ À régler"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.4rem", borderTop: "1px dashed #e8d5c4", fontSize: "0.85rem" }}>
                  <span style={{ color: "#5a5a5a" }}>Contrat de location</span>
                  {b.contract_signed ? (
                    <span data-testid={`drawer-contract-signed-${b.id}`} style={{ fontWeight: 600, color: "#2d7a4f" }}>
                      ✓ Signé{b.contract_signed_by ? ` — ${b.contract_signed_by}` : ""}
                    </span>
                  ) : (
                    <span style={{ fontWeight: 600, color: "#f59e0b" }}>⏳ Non signé</span>
                  )}
                </div>
                {b.contract_signed && b.quote_token && (
                  <a
                    href={`${API}/quotes/${b.quote_token}/contract-pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`drawer-contract-pdf-${b.id}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: "0.6rem", fontSize: "0.78rem", color: "#b8977e", textDecoration: "none", fontWeight: 600 }}
                  >
                    📄 Télécharger le contrat signé
                  </a>
                )}
                {b.deposit_paid && !b.balance_paid && (
                  <button
                    data-testid={`drawer-send-balance-link-${b.id}`}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/leads/${b.id}/send-balance-link`);
                        toast.success("Lien de paiement solde envoyé ✓");
                      } catch (e) {
                        toast.error("Erreur lors de l'envoi");
                      }
                    }}
                    style={{
                      width: "100%",
                      marginTop: "0.7rem",
                      padding: "0.7rem 0.9rem",
                      background: "transparent",
                      border: "1.5px solid #b8977e",
                      borderRadius: 50,
                      color: "#b8977e",
                      fontFamily: "'DM Sans',sans-serif",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      cursor: "pointer",
                      transition: "all .2s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#b8977e"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#b8977e"; }}
                  >
                    💳 Envoyer lien paiement solde ({Number(b.balance_amount || 0).toFixed(2)}€)
                  </button>
                )}
                {b.balance_paid && (
                  <div data-testid={`drawer-balance-paid-${b.id}`} style={{ marginTop: "0.7rem", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "0.5rem 0.7rem", textAlign: "center" }}>
                    <span style={{ color: "#2d7a4f", fontWeight: 600, fontSize: "0.78rem" }}>
                      ✅ Solde réglé{b.balance_paid_at ? ` le ${new Date(b.balance_paid_at).toLocaleDateString("fr-FR")}` : ""}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Parcours client timeline */}
            {(() => {
              const fmt = (iso) => {
                if (!iso) return null;
                try {
                  const d = new Date(iso);
                  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
                    + " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                } catch { return null; }
              };
              const fmtDate = (s) => {
                if (!s) return null;
                try { return new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
                catch { return s; }
              };
              const paidLabel = {
                stripe: "Stripe", whatsapp: "WhatsApp", virement: "Virement", cash: "Espèces",
              };
              const steps = [
                {
                  key: "created",
                  label: "Devis envoyé",
                  done: !!b.created_at,
                  when: fmt(b.created_at),
                  source: "Auto",
                },
                {
                  key: "signed",
                  label: "Devis signé",
                  done: !!b.signed,
                  when: b.signed ? fmt(b.signed_at || b.created_at) : "En attente",
                  source: b.signed ? (b.signed_by ? "Client" : "Admin") : null,
                },
                {
                  key: "paid",
                  label: "Paiement",
                  done: b.payment_status === "paid",
                  when: b.payment_status === "paid"
                    ? fmt(b.paid_at || b.updated_at) + (b.payment_method ? " — " + (paidLabel[b.payment_method] || b.payment_method) : "")
                    : b.payment_status === "confirmed" ? "Confirmé, en attente de paiement" : "En attente",
                  source: b.payment_status === "paid" ? "Auto" : null,
                },
                {
                  key: "event",
                  label: "Événement",
                  done: b.event_date && new Date(b.event_date) < new Date(),
                  when: fmtDate(b.event_date) || "—",
                  source: null,
                },
                {
                  key: "review",
                  label: "Avis client",
                  done: !!b.review_id,
                  when: b.review_id ? "Avis laissé" : "—",
                  source: b.review_id ? "Client" : null,
                },
              ];
              const sourceStyle = (s) => {
                if (!s) return null;
                const map = {
                  Auto: { bg: "#faf8f5", color: "#b8977e" },
                  Client: { bg: "#f0f9f2", color: "#2d7a4f" },
                  Admin: { bg: "#fef3ea", color: "#b8977e" },
                };
                return map[s] || map.Auto;
              };
              return (
                <div style={{ marginBottom: "1.2rem" }} data-testid="parcours-timeline">
                  <label style={{ ...filterLabelStyle, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    <span style={{ fontSize: "12px" }}>📋</span> Parcours client
                  </label>
                  <div style={{ background: "#fff", border: `1px solid #e8d5c4`, borderRadius: 8, padding: "12px 14px" }}>
                    {steps.map((s, idx) => (
                      <div key={s.key} style={{ display: "flex", gap: "10px", position: "relative", paddingBottom: idx === steps.length - 1 ? 0 : "14px" }}>
                        {/* Vertical line + dot */}
                        <div style={{ position: "relative", width: 14, flexShrink: 0 }}>
                          <span style={{
                            position: "absolute", left: 3, top: 4, width: 10, height: 10, borderRadius: "50%",
                            background: s.done ? "#b8977e" : "#fff",
                            border: `1.5px solid ${s.done ? "#b8977e" : "#d4c4b3"}`,
                            zIndex: 1,
                          }} />
                          {idx < steps.length - 1 && (
                            <span style={{
                              position: "absolute", left: 7, top: 14, bottom: -4, width: 1,
                              background: s.done ? "#b8977e" : "#e8d5c4",
                            }} />
                          )}
                        </div>
                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span style={{
                              fontSize: "0.82rem", fontWeight: 600,
                              color: s.done ? "#1a1a1a" : "#999",
                              fontFamily: "'DM Sans',sans-serif",
                            }}>
                              {s.label}
                            </span>
                            {s.done && s.key === "signed" && <span style={{ color: "#2d7a4f", fontSize: "0.75rem" }}>✓</span>}
                            {s.source && (
                              <span style={{
                                fontSize: "0.65rem", padding: "1px 7px", borderRadius: 999,
                                fontFamily: "'Montserrat',sans-serif", fontWeight: 600,
                                letterSpacing: "0.04em", textTransform: "uppercase",
                                background: sourceStyle(s.source).bg, color: sourceStyle(s.source).color,
                              }}>{s.source}</span>
                            )}
                          </div>
                          <div style={{
                            fontSize: "0.72rem", color: s.done ? "#5a5a5a" : "#b0b0b0",
                            fontFamily: "'Montserrat','DM Sans',sans-serif", marginTop: 2,
                          }}>
                            {s.when || "—"}
                          </div>
                          {s.done && s.key === "signed" && b.signed_by && (
                            <div style={{ fontSize: "0.7rem", color: "#777", marginTop: 1 }}>
                              Par : {b.signed_by}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Signature section */}
            <div style={{ marginBottom: "1.2rem" }}>
              <label style={{ ...filterLabelStyle, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <span style={{ fontSize: "12px" }}>📝</span> Signature du devis
              </label>
              {b.signed ? (
                <div
                  data-testid="signature-signed-state"
                  style={{
                    background: "#faf8f5",
                    border: "1px solid #e8d5c4",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#2d7a4f", fontSize: "0.8rem", fontWeight: 600, marginBottom: "6px" }}>
                    <span>✅</span> Devis signé
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#5a5a5a", fontFamily: "'Montserrat','DM Sans',sans-serif" }}>
                    Par : <strong style={{ color: "#1a1a1a" }}>{b.signed_by || "—"}</strong>
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#5a5a5a", fontFamily: "'Montserrat','DM Sans',sans-serif" }}>
                    Le : <strong style={{ color: "#1a1a1a" }}>{b.signed_display || (b.signed_at ? new Date(b.signed_at).toLocaleDateString("fr-FR") : "—")}</strong>
                  </div>
                </div>
              ) : (
                <div
                  data-testid="signature-unsigned-state"
                  style={{
                    background: "#faf8f5",
                    border: "1px solid #e8d5c4",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                >
                  <input
                    type="text"
                    data-testid="signature-signed-by-input"
                    placeholder="Signé par (Prénom Nom)"
                    value={signInputs[b.id]?.by || ""}
                    onChange={(e) => setSignInputs((p) => ({ ...p, [b.id]: { ...(p[b.id] || {}), by: e.target.value } }))}
                    style={{ ...filterInputStyle, marginBottom: "6px", fontSize: "0.8rem" }}
                  />
                  <input
                    type="date"
                    data-testid="signature-signed-date-input"
                    value={signInputs[b.id]?.date || new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setSignInputs((p) => ({ ...p, [b.id]: { ...(p[b.id] || {}), date: e.target.value } }))}
                    style={{ ...filterInputStyle, marginBottom: "8px", fontSize: "0.8rem" }}
                  />
                  <button
                    data-testid="signature-confirm-btn"
                    onClick={() => markSigned(b.id)}
                    style={{
                      width: "100%",
                      background: "#b8977e",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    Confirmer la signature
                  </button>
                </div>
              )}
            </div>

            {/* Admin notes */}
            <div style={{ marginBottom: "1.2rem" }}>
              <label style={{ ...filterLabelStyle, display: "flex", alignItems: "center", gap: "0.3rem" }}><StickyNote style={{ width: 12, height: 12 }} /> Notes internes</label>
              <textarea
                value={noteVal}
                onChange={e => setNotes(prev => ({ ...prev, [b.id]: e.target.value }))}
                placeholder="Notes visibles admin uniquement..."
                rows={3}
                style={{ ...filterInputStyle, resize: "none", fontSize: "0.8rem" }}
                data-testid="drawer-notes"
              />
              <button onClick={() => saveNote(b.id, notes[b.id] || "")} style={{ marginTop: "0.3rem", fontSize: "0.75rem", color: "#b8977e", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                Enregistrer la note
              </button>
            </div>

            {/* Actions */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
              <button onClick={() => { onGenerateQuote(b); }} style={drawerBtnStyle}><FileText style={{ width: 14, height: 14 }} /> Devis PDF</button>
              <button onClick={() => { onGenerateInvoice(b); }} style={drawerBtnStyle}><Receipt style={{ width: 14, height: 14 }} /> Facture PDF</button>
              <button onClick={() => { openInvoiceModal(b); }} style={{ ...drawerBtnStyle, background: "#fef9f5", borderColor: "#b8977e", color: "#b8977e" }}><Mail style={{ width: 14, height: 14 }} /> Envoyer facture</button>
              <button onClick={() => { onEdit(b); }} style={drawerBtnStyle}><Edit style={{ width: 14, height: 14 }} /> Modifier</button>
              <button onClick={() => { setDrawerBooking(null); onDelete(b); }} style={{ ...drawerBtnStyle, color: "#ef4444", borderColor: "#fca5a5" }}><Trash2 style={{ width: 14, height: 14 }} /> Supprimer</button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ===== BOARD VIEW =====
  const BoardView = () => (
    <div style={{ display: "flex", gap: "1rem", overflowX: "auto", paddingBottom: "1rem", minHeight: 400 }}>
      {STATUS_ORDER.map(status => {
        const col = filtered.filter(b => b.payment_status === status);
        const st = STATUS_CONFIG[status];
        return (
          <div
            key={status}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, status)}
            style={{ flex: "1 1 0", minWidth: 280, background: "#fafafa", borderRadius: "1rem", padding: "0.75rem", border: "1.5px solid #f0e4da" }}
            data-testid={`board-col-${status}`}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", padding: "0 0.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: st.color }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a", fontSize: "0.9rem" }}>{st.label}</span>
              </div>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", fontWeight: 700, background: st.bg, color: st.color, padding: "0.15rem 0.6rem", borderRadius: 50 }}>{col.length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {col.map(b => <BookingCard key={b.id} b={b} />)}
              {col.length === 0 && <p style={{ textAlign: "center", fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#ccc", padding: "2rem 0" }}>Aucune réservation</p>}
            </div>
          </div>
        );
      })}
    </div>
  );

  // ===== LIST VIEW =====
  const ListViewTab = () => (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem" }}>
        <thead>
          <tr style={{ background: "#1a1a1a" }}>
            {[
              { key: "event_date", label: "Date" },
              { key: "customer_name", label: "Client" },
              { key: "package_name", label: "Forfait" },
              { key: "event_location", label: "Lieu" },
              { key: "payment_status", label: "Statut" },
              { key: "total_price", label: "Prix" },
              { key: null, label: "Actions" },
            ].map(h => (
              <th
                key={h.label}
                onClick={() => h.key && toggleSort(h.key)}
                style={{ padding: "0.7rem 0.75rem", color: "#fff", fontWeight: 600, textAlign: "left", cursor: h.key ? "pointer" : "default", whiteSpace: "nowrap", fontSize: "0.8rem" }}
              >
                {h.label} {sortCol === h.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((b, i) => {
            const price = calculatePriceWithPromotion(b);
            const st = STATUS_CONFIG[b.payment_status] || STATUS_CONFIG.pending;
            return (
              <tr key={b.id} onClick={() => setDrawerBooking(b)} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer", borderBottom: "1px solid #f0e4da" }} className="hover:bg-[#fef9f5]">
                <td style={{ padding: "0.6rem 0.75rem" }}>{b.event_date ? format(parseISO(b.event_date), "dd MMM yy", { locale: fr }) : "—"}</td>
                <td style={{ padding: "0.6rem 0.75rem", fontWeight: 500 }}>{b.customer_name}</td>
                <td style={{ padding: "0.6rem 0.75rem" }}>{b.package_name || "—"}</td>
                <td style={{ padding: "0.6rem 0.75rem" }}>{b.event_location}</td>
                <td style={{ padding: "0.6rem 0.75rem" }}>
                  <span style={{ fontSize: "0.7rem", fontWeight: 600, padding: "0.2rem 0.5rem", borderRadius: 50, background: st.bg, color: st.color }}>{st.label}</span>
                </td>
                <td style={{ padding: "0.6rem 0.75rem", fontWeight: 600, color: "#b8977e" }}>{price.final.toFixed(0)}€</td>
                <td style={{ padding: "0.6rem 0.75rem" }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    <button onClick={() => onGenerateQuote(b)} style={iconBtnStyle}><FileText style={{ width: 13, height: 13 }} /></button>
                    <button onClick={() => onEdit(b)} style={iconBtnStyle}><Edit style={{ width: 13, height: 13 }} /></button>
                    <button onClick={() => onDelete(b)} style={{ ...iconBtnStyle, color: "#ef4444" }}><Trash2 style={{ width: 13, height: 13 }} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // ===== CALENDAR VIEW =====
  const CalendarViewTab = () => {
    const monthStart = startOfMonth(calMonth);
    const monthEnd = endOfMonth(calMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: calStart, end: calEnd });
    const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <button onClick={() => setCalMonth(subMonths(calMonth, 1))} style={{ ...iconBtnStyle, width: 32, height: 32 }}><ChevronLeft style={{ width: 16, height: 16 }} /></button>
          <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.1rem" }}>{format(calMonth, "MMMM yyyy", { locale: fr })}</span>
          <button onClick={() => setCalMonth(addMonths(calMonth, 1))} style={{ ...iconBtnStyle, width: 32, height: 32 }}><ChevronRight style={{ width: 16, height: 16 }} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {dayNames.map(d => <div key={d} style={{ textAlign: "center", fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "#999", padding: "0.4rem" }}>{d}</div>)}
          {days.map(day => {
            const dateStr = format(day, "yyyy-MM-dd");
            const inMonth = day.getMonth() === calMonth.getMonth();
            const dayBookings = bookings.filter(b => b.event_date === dateStr);
            const cnt = dateBoothCount[dateStr] || 0;
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={dateStr}
                style={{ minHeight: 70, borderRadius: "0.5rem", padding: "0.3rem", background: !inMonth ? "#f5f5f5" : cnt >= 2 ? "#fee2e2" : cnt === 1 ? "#fff7ed" : "#fff", border: isToday ? "2px solid #b8977e" : "1px solid #f0e4da", opacity: inMonth ? 1 : 0.4, position: "relative" }}
                title={dayBookings.map(b => `${b.customer_name} - ${b.package_name}`).join("\n")}
              >
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", fontWeight: isToday ? 700 : 400, color: "#1a1a1a" }}>{format(day, "d")}</span>
                {dayBookings.slice(0, 2).map((b, i) => (
                  <div
                    key={b.id}
                    onClick={() => setDrawerBooking(b)}
                    style={{ fontSize: "0.6rem", fontFamily: "'DM Sans',sans-serif", background: STATUS_CONFIG[b.payment_status]?.bg || "#eee", color: STATUS_CONFIG[b.payment_status]?.color || "#333", borderRadius: 4, padding: "0.1rem 0.3rem", marginTop: 2, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {b.customer_name?.split(" ")[0]}
                  </div>
                ))}
                {dayBookings.length > 2 && <span style={{ fontSize: "0.55rem", color: "#999" }}>+{dayBookings.length - 2}</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif" }}>
      {/* Top bar */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ ...iconBtnStyle, width: 36, height: 36, background: sidebarOpen ? "#fef9f5" : "#fff", position: "relative" }} data-testid="toggle-filters">
            <Filter style={{ width: 16, height: 16 }} />
            {activeFilterCount > 0 && <span style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", background: "#b8977e", color: "#fff", fontSize: "0.6rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{activeFilterCount}</span>}
          </button>
          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#777" }}>
            {totalCount} réservation{totalCount > 1 ? "s" : ""}
            {nextFullDate ? ` — 2/2 PB le ${format(parseISO(nextFullDate[0]), "d MMM", { locale: fr })}` : ""}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          {/* View toggles */}
          {[
            { id: "board", icon: LayoutGrid, label: "Board" },
            { id: "list", icon: List, label: "Liste" },
            { id: "calendar", icon: CalendarDays, label: "Calendrier" },
          ].map(v => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              style={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: "0.4rem 0.8rem", borderRadius: 50, border: view === v.id ? "1.5px solid #b8977e" : "1.5px solid #e8d5c4", background: view === v.id ? "#fef9f5" : "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", fontWeight: view === v.id ? 600 : 400, color: view === v.id ? "#b8977e" : "#777", cursor: "pointer" }}
              data-testid={`view-${v.id}`}
            >
              <v.icon style={{ width: 14, height: 14 }} /> {v.label}
            </button>
          ))}

          {view === "list" && (
            <button onClick={exportCSV} style={{ ...iconBtnStyle, width: 32, height: 32 }} title="Export CSV"><Download style={{ width: 14, height: 14 }} /></button>
          )}
        </div>
      </div>

      {/* Main layout */}
      <div style={{ display: "flex", gap: 0 }}>
        <Sidebar />
        <div style={{ flex: 1, minWidth: 0 }}>
          {view === "board" && <BoardView />}
          {view === "list" && <ListViewTab />}
          {view === "calendar" && <CalendarViewTab />}
        </div>
      </div>

      <Drawer />

      {/* Invoice Email Modal */}
      {invoiceModal && (
        <>
          <div onClick={() => setInvoiceModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 60 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "#fff", borderRadius: "1.5rem", padding: "2rem", width: "min(420px, 90vw)", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 61 }} data-testid="invoice-email-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.2rem" }}>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.15rem", color: "#1a1a1a" }}>Envoyer la facture</h3>
              <button onClick={() => setInvoiceModal(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#999" }}><X style={{ width: 18, height: 18 }} /></button>
            </div>

            <div style={{ background: "#f5ede8", borderRadius: "0.75rem", padding: "1rem", marginBottom: "1rem" }}>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a", fontSize: "0.9rem" }}>{invoiceModal.booking.customer_name}</p>
              <p style={{ fontFamily: "'DM Sans',sans-serif", color: "#777", fontSize: "0.8rem" }}>{invoiceModal.booking.package_name} — {invoiceModal.booking.total_price}€</p>
              <p style={{ fontFamily: "'DM Sans',sans-serif", color: "#999", fontSize: "0.75rem" }}>{invoiceModal.booking.event_date}</p>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.3rem" }}>Email du destinataire</label>
              <input
                type="email"
                value={invoiceModal.email}
                onChange={(e) => setInvoiceModal({ ...invoiceModal, email: e.target.value })}
                style={{ width: "100%", fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", padding: "0.7rem 0.8rem", borderRadius: "0.5rem", border: "1.5px solid #e8d5c4", outline: "none" }}
                data-testid="invoice-email-input"
              />
            </div>

            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#999", marginBottom: "1.2rem" }}>
              <Mail style={{ width: 13, height: 13, display: "inline", verticalAlign: "middle", marginRight: 4 }} />
              Facture_SelfieBooth sera envoyée à <strong style={{ color: "#b8977e" }}>{invoiceModal.email}</strong>
            </p>

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={() => setInvoiceModal(null)} style={{ flex: 1, padding: "0.7rem", borderRadius: 50, border: "1.5px solid #e8d5c4", background: "#fff", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#777", cursor: "pointer", fontSize: "0.85rem" }}>
                Annuler
              </button>
              <button
                onClick={sendInvoiceEmail}
                disabled={sendingInvoice || !invoiceModal.email}
                style={{ flex: 2, padding: "0.7rem", borderRadius: 50, border: "none", background: sendingInvoice ? "#ccc" : "linear-gradient(135deg, #b8977e, #c9a88a)", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#fff", cursor: sendingInvoice ? "not-allowed" : "pointer", fontSize: "0.85rem" }}
                data-testid="confirm-send-invoice"
              >
                {sendingInvoice ? "Envoi en cours..." : "Confirmer l'envoi"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ===== SHARED STYLES =====
const iconBtnStyle = { background: "#fff", border: "1px solid #e8d5c4", borderRadius: "0.4rem", padding: "0.35rem", cursor: "pointer", color: "#b8977e", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" };
const filterLabelStyle = { fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", fontWeight: 600, color: "#777", display: "block", marginBottom: "0.3rem" };
const filterInputStyle = { width: "100%", fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", padding: "0.5rem 0.65rem", borderRadius: "0.5rem", border: "1.5px solid #e8d5c4", outline: "none", background: "#fff" };
const drawerBtnStyle = { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", padding: "0.6rem", borderRadius: "0.5rem", border: "1.5px solid #e8d5c4", background: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", fontWeight: 500, color: "#1a1a1a", cursor: "pointer" };
